import { Component, signal, input, output, computed, ChangeDetectionStrategy, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NotificationItemComponent, type Notification } from './notification-item.component';
import { NavigationService } from '@services/navigation.service';

@Component({
  selector: 'app-notifications-sidebar',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, NotificationItemComponent],
  template: `
    <!-- Overlay -->
    @if (isOpen()) {
      <div class="notifications-overlay" (click)="closeSidebar()" (keydown.escape)="closeSidebar()"></div>
    }

    <!-- Sidebar -->
    <aside
      [class]="'notifications-sidebar ' + (isOpen() ? 'open' : 'closed')"
      [attr.aria-label]="'Panel de notificaciones'"
      role="complementary"
    >
      <!-- Header -->
      <header class="sidebar-header">
        <div class="header-content">
          <div class="header-title">
            <h2>Notificaciones</h2>
            @if (unreadCount() > 0) {
              <span class="unread-badge">{{ unreadCount() }}</span>
            }
          </div>

          <div class="header-actions">
            @if (unreadCount() > 0) {
              <button
                class="action-btn"
                (click)="onMarkAllAsRead()"
                aria-label="Marcar todas como leídas"
                type="button"
              >
                <i class="fas fa-check-double"></i>
              </button>
            }

            <button class="action-btn" (click)="onClearAll()" aria-label="Limpiar todas" type="button">
              <i class="fas fa-trash"></i>
            </button>

            <button class="close-btn" (click)="closeSidebar()" aria-label="Cerrar panel" type="button">
              <i class="fas fa-times"></i>
            </button>
          </div>
        </div>
      </header>

      <!-- Filter Tabs -->
      <nav class="filter-tabs" role="tablist">
        @for (filter of filterOptions; track filter.key) {
          <button
            class="filter-tab"
            [class.active]="activeFilter() === filter.key"
            (click)="setActiveFilter(filter.key)"
            [attr.aria-selected]="activeFilter() === filter.key"
            role="tab"
            type="button"
          >
            <span>{{ filter.label }}</span>
            @if (filter.key === 'unread' && unreadCount() > 0) {
              <span class="tab-badge">{{ unreadCount() }}</span>
            }
          </button>
        }
      </nav>

      <!-- Content -->
      <main class="sidebar-content" role="tabpanel">
        @if (filteredNotifications().length === 0) {
          <div class="empty-state">
            <i class="fas fa-bell-slash empty-icon"></i>
            <h3>{{ getEmptyMessage() }}</h3>
            <p>{{ getEmptyDescription() }}</p>
          </div>
        } @else {
          <div class="notifications-list">
            @for (notification of filteredNotifications(); track notification.id) {
              <app-notification-item
                [notification]="notification"
                (notificationClick)="onNotificationClick($event)"
                (markAsRead)="onMarkAsRead($event)"
                (actionClick)="onActionClick($event)"
              >
              </app-notification-item>
            }
          </div>
        }
      </main>

      <!-- Footer -->
      <footer class="sidebar-footer">
        <button class="view-all-btn" (click)="navigateToNotifications()" type="button">
          <i class="fas fa-external-link-alt"></i>
          <span>Ver todas las notificaciones</span>
        </button>
      </footer>
    </aside>
  `,
  styles: [
    `
      /* Overlay */
      .notifications-overlay {
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: rgba(0, 0, 0, 0.3);
        backdrop-filter: blur(4px);
        z-index: 1100;
        opacity: 0;
        animation: overlayIn 0.3s ease forwards;
      }

      @keyframes overlayIn {
        to {
          opacity: 1;
        }
      }

      /* Sidebar */
      .notifications-sidebar {
        position: fixed;
        top: 0;
        right: 0;
        height: 100vh;
        width: 500px;
        max-width: 100vw;
        background: var(--bg-primary);
        border-left: 1px solid var(--border-primary);
        box-shadow: var(--shadow-xl);
        z-index: 1200;
        display: flex;
        flex-direction: column;
        transform: translateX(100%);
        transition:
          transform 0.3s ease,
          box-shadow 0.3s ease;
        overflow: hidden;
      }

      .notifications-sidebar.open {
        transform: translateX(0);
      }

      .notifications-sidebar.closed {
        transform: translateX(100%);
      }

      /* Header */
      .sidebar-header {
        flex-shrink: 0;
        background: linear-gradient(135deg, var(--bg-primary) 0%, var(--bg-secondary) 100%);
        border-bottom: 1px solid var(--border-primary);
        padding: var(--space-xl);
        box-shadow: var(--shadow-sm);
      }

      .header-content {
        display: flex;
        justify-content: space-between;
        align-items: center;
        gap: var(--space-md);
      }

      .header-title {
        display: flex;
        align-items: center;
        gap: var(--space-md);
        flex: 1;
      }

      .header-title h2 {
        font-size: var(--text-xl);
        font-weight: var(--font-semibold);
        color: var(--text-primary);
        margin: 0;
        line-height: var(--leading-snug);
      }

      .unread-badge {
        background: var(--red-500);
        color: var(--text-inverse);
        padding: var(--space-xs) var(--space-sm);
        border-radius: var(--radius-full);
        font-size: var(--text-xs);
        font-weight: var(--font-bold);
        min-width: 20px;
        height: 20px;
        display: flex;
        align-items: center;
        justify-content: center;
        line-height: 1;
      }

      .header-actions {
        display: flex;
        align-items: center;
        gap: var(--space-xs);
      }

      .action-btn,
      .close-btn {
        width: 36px;
        height: 36px;
        display: flex;
        align-items: center;
        justify-content: center;
        background: none;
        border: none;
        color: var(--text-muted);
        cursor: pointer;
        border-radius: var(--radius-md);
        transition: all 0.2s ease;
        font-size: var(--text-sm);
      }

      .action-btn:hover,
      .close-btn:hover {
        background: var(--bg-secondary);
        color: var(--text-primary);
        transform: scale(1.1);
      }

      .close-btn:hover {
        color: var(--red-600);
      }

      .action-btn:focus,
      .close-btn:focus {
        outline: 2px solid var(--red-500);
        outline-offset: 2px;
      }

      /* Filter Tabs */
      .filter-tabs {
        flex-shrink: 0;
        display: flex;
        background: var(--bg-secondary);
        border-bottom: 1px solid var(--border-primary);
        overflow-x: auto;
      }

      .filter-tabs::-webkit-scrollbar {
        height: 2px;
      }

      .filter-tabs::-webkit-scrollbar-track {
        background: var(--bg-muted);
      }

      .filter-tabs::-webkit-scrollbar-thumb {
        background: var(--text-muted);
        border-radius: var(--radius-xs);
      }

      .filter-tab {
        flex: 1;
        display: flex;
        align-items: center;
        justify-content: center;
        gap: var(--space-xs);
        padding: var(--space-md) var(--space-lg);
        background: none;
        border: none;
        color: var(--text-muted);
        cursor: pointer;
        font-size: var(--text-sm);
        font-weight: var(--font-medium);
        transition: all 0.2s ease;
        border-bottom: 2px solid transparent;
        white-space: nowrap;
        min-width: 0;
      }

      .filter-tab:hover {
        background: var(--bg-tertiary);
        color: var(--text-primary);
      }

      .filter-tab.active {
        color: var(--red-600);
        border-bottom-color: var(--red-500);
        background: var(--bg-primary);
        font-weight: var(--font-semibold);
      }

      .tab-badge {
        background: var(--red-500);
        color: var(--text-inverse);
        padding: 2px 6px;
        border-radius: var(--radius-full);
        font-size: 10px;
        font-weight: var(--font-bold);
        min-width: 16px;
        height: 16px;
        display: flex;
        align-items: center;
        justify-content: center;
        line-height: 1;
      }

      /* Content */
      .sidebar-content {
        flex: 1;
        overflow-y: auto;
        overflow-x: hidden;
        min-height: 0;
      }

      .sidebar-content::-webkit-scrollbar {
        width: 6px;
      }

      .sidebar-content::-webkit-scrollbar-track {
        background: var(--bg-secondary);
      }

      .sidebar-content::-webkit-scrollbar-thumb {
        background: var(--text-muted);
        border-radius: var(--radius-xs);
      }

      .sidebar-content::-webkit-scrollbar-thumb:hover {
        background: var(--text-secondary);
      }

      .notifications-list {
        display: flex;
        flex-direction: column;
        width: 100%;
        min-width: 0;
      }

      /* Empty State */
      .empty-state {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        padding: var(--space-4xl) var(--space-xl);
        text-align: center;
        min-height: 300px;
      }

      .empty-icon {
        font-size: var(--text-4xl);
        color: var(--text-muted);
        margin-bottom: var(--space-xl);
        opacity: 0.6;
      }

      .empty-state h3 {
        font-size: var(--text-lg);
        font-weight: var(--font-semibold);
        color: var(--text-primary);
        margin: 0 0 var(--space-sm) 0;
        line-height: var(--leading-snug);
      }

      .empty-state p {
        font-size: var(--text-sm);
        color: var(--text-muted);
        line-height: var(--leading-relaxed);
        margin: 0;
        max-width: 280px;
      }

      /* Footer */
      .sidebar-footer {
        flex-shrink: 0;
        padding: var(--space-lg);
        border-top: 1px solid var(--border-primary);
        background: var(--bg-secondary);
      }

      .view-all-btn {
        width: 100%;
        display: flex;
        align-items: center;
        justify-content: center;
        gap: var(--space-sm);
        padding: var(--space-md) var(--space-lg);
        background: var(--bg-primary);
        border: 1px solid var(--border-primary);
        border-radius: var(--radius-md);
        color: var(--text-secondary);
        font-size: var(--text-sm);
        font-weight: var(--font-medium);
        cursor: pointer;
        transition: all 0.2s ease;
        text-decoration: none;
      }

      .view-all-btn:hover {
        background: var(--bg-accent);
        border-color: var(--border-accent);
        color: var(--red-600);
        transform: translateY(-1px);
        box-shadow: var(--shadow-sm);
      }

      .view-all-btn:focus {
        outline: 2px solid var(--red-500);
        outline-offset: 2px;
      }

      .view-all-btn i {
        font-size: var(--text-xs);
      }

      /* Mobile Styles */
      @media (max-width: 768px) {
        .notifications-sidebar {
          width: 100vw;
          max-width: none;
          top: 0; /* Pantalla completa desde arriba */
          height: 100vh; /* Altura completa de la pantalla */
          z-index: 1300; /* Más alto que el header */
        }

        .notifications-overlay {
          z-index: 1250; /* Entre el header y la sidebar */
          top: 0; /* También pantalla completa */
        }

        .sidebar-header {
          padding: var(--space-lg);
        }

        .header-title h2 {
          font-size: var(--text-lg);
        }

        .filter-tab {
          padding: var(--space-sm) var(--space-md);
          font-size: var(--text-xs);
        }

        .empty-state {
          padding: var(--space-2xl) var(--space-lg);
          min-height: 200px;
        }

        .empty-icon {
          font-size: var(--text-3xl);
        }

        .sidebar-footer {
          padding: var(--space-md);
        }
      }

      @media (max-width: 480px) {
        .notifications-sidebar {
          top: 0; /* Pantalla completa también en móviles pequeños */
          height: 100vh; /* Altura completa */
        }
      }

      /* Reduced motion support */
      @media (prefers-reduced-motion: reduce) {
        .notifications-sidebar,
        .notifications-overlay,
        .action-btn,
        .close-btn,
        .filter-tab,
        .view-all-btn {
          transition: none;
          animation: none;
        }

        .action-btn:hover,
        .close-btn:hover {
          transform: none;
        }

        .view-all-btn:hover {
          transform: none;
        }
      }

      /* High contrast mode */
      @media (prefers-contrast: high) {
        .notifications-sidebar {
          border-left-width: 2px;
        }

        .sidebar-header,
        .filter-tabs,
        .sidebar-footer {
          border-bottom-width: 2px;
          border-top-width: 2px;
        }
      }
    `,
  ],
})
export class NotificationsSidebarComponent {
  private navigationService = inject(NavigationService);

  // Input properties
  isOpen = input<boolean>(false);
  notifications = input<Notification[]>([]);

  // Output events
  close = output<void>();
  notificationClick = output<Notification>();
  markAsRead = output<string>();
  markAllAsRead = output<void>();
  clearAll = output<void>();
  actionClick = output<{ notificationId: string; actionUrl: string }>();

  // Internal state
  activeFilter = signal<'all' | 'unread' | 'info' | 'success' | 'warning' | 'error'>('all');

  // Filter options
  filterOptions = [
    { key: 'all' as const, label: 'Todas', icon: 'fas fa-list' },
    { key: 'unread' as const, label: 'No leídas', icon: 'fas fa-circle' },
    { key: 'info' as const, label: 'Info', icon: 'fas fa-info-circle' },
    { key: 'success' as const, label: 'Éxito', icon: 'fas fa-check-circle' },
    { key: 'warning' as const, label: 'Advertencia', icon: 'fas fa-exclamation-triangle' },
    { key: 'error' as const, label: 'Error', icon: 'fas fa-times-circle' },
  ];

  // Computed properties
  unreadCount = computed(() => {
    return this.notifications().filter((n) => !n.isRead).length;
  });

  filteredNotifications = computed(() => {
    const filter = this.activeFilter();
    const notifications = this.notifications();

    switch (filter) {
      case 'unread':
        return notifications.filter((n) => !n.isRead);
      case 'info':
      case 'success':
      case 'warning':
      case 'error':
        return notifications.filter((n) => n.type === filter);
      case 'all':
      default:
        return notifications;
    }
  });

  // Methods
  closeSidebar(): void {
    this.close.emit();
  }

  setActiveFilter(filter: 'all' | 'unread' | 'info' | 'success' | 'warning' | 'error'): void {
    this.activeFilter.set(filter);
  }

  onNotificationClick(notification: Notification): void {
    this.notificationClick.emit(notification);
  }

  onMarkAsRead(notificationId: string): void {
    this.markAsRead.emit(notificationId);
  }

  onActionClick(event: { notificationId: string; actionUrl: string }): void {
    this.actionClick.emit(event);
  }

  onMarkAllAsRead(): void {
    this.markAllAsRead.emit();
  }

  onClearAll(): void {
    this.clearAll.emit();
  }

  navigateToNotifications(): void {
    this.navigationService.navigate(['/notifications']);
    this.closeSidebar();
  }

  getEmptyMessage(): string {
    const filter = this.activeFilter();
    switch (filter) {
      case 'unread':
        return 'No tienes notificaciones sin leer';
      case 'info':
        return 'No tienes notificaciones informativas';
      case 'success':
        return 'No tienes notificaciones de éxito';
      case 'warning':
        return 'No tienes notificaciones de advertencia';
      case 'error':
        return 'No tienes notificaciones de error';
      case 'all':
      default:
        return 'No tienes notificaciones';
    }
  }

  getEmptyDescription(): string {
    const filter = this.activeFilter();
    switch (filter) {
      case 'unread':
        return 'Todas las notificaciones han sido leídas.';
      case 'info':
      case 'success':
      case 'warning':
      case 'error':
        return 'No hay notificaciones de este tipo.';
      case 'all':
      default:
        return 'Te notificaremos cuando tengas nuevas actualizaciones.';
    }
  }
}
